interface TidalHeightsFigureProps {
  size?: number;
}

/// Cross-section reference diagram of the standard tidal datums
/// plus the practical measurements a navigator needs (charted
/// depth, drying height, height of tide, sounding, vertical
/// clearance). One slice through a coastline: deep water on the
/// left, drying rock + shore on the right, a bridge across the
/// top. Annotations on both sides show what each measurement is
/// referenced to.
///
/// Coordinate convention on the 600 × 480 viewBox:
///   - x grows left → right (seaward → shoreward)
///   - y grows top → bottom (sky → seabed)
///   - all tidal-level horizontal lines are pinned at fixed y
///     coordinates so the legend stays consistent regardless of
///     how the page resizes
export function TidalHeightsFigure({ size = 560 }: TidalHeightsFigureProps) {
  // Fixed y coordinates for each tidal level. Mediocre east-coast
  // UK profile — 4 m springs range, 2 m neaps range, asymmetric
  // about ML so the picture is realistic.
  const Y_HAT = 80;
  const Y_MHWS = 100;
  const Y_MHWN = 130;
  const Y_ML = 170;
  const Y_MLWN = 210;
  const Y_MLWS = 240;
  const Y_LAT = 255;
  const Y_CD = 260;
  const Y_BRIDGE_TOP = 30;
  const Y_BRIDGE_BOTTOM = 65;

  // Where we're pretending the current tide is — between MHWN and
  // MHWS, illustrating a typical "half-flood" reading.
  const Y_WATER = 115;

  // Seabed profile — deep on the left, shoaling into a drying rock
  // and then onto the beach on the right.
  const seabedPath =
    `M 0 ${Y_CD + 60} ` +
    `L 60 ${Y_CD + 60} ` +
    `C 90 ${Y_CD + 55}, 140 ${Y_CD + 30}, 210 ${Y_CD + 30} ` +
    `C 260 ${Y_CD + 30}, 290 ${Y_CD + 15}, 320 ${Y_CD + 15} ` + // approach
    `C 360 ${Y_CD + 15}, 380 ${Y_CD - 5}, 400 ${Y_CD - 15} ` + // drying rock peak
    `C 415 ${Y_CD - 5}, 430 ${Y_CD + 5}, 460 ${Y_CD + 5} ` +
    `C 500 ${Y_CD + 5}, 540 ${Y_CD - 30}, 600 ${Y_CD - 60} ` + // beach climbs ashore
    `L 600 ${Y_CD + 60} Z`;

  // Drying-rock peak label coordinates.
  const DRYING_X = 400;
  const DRYING_TOP_Y = Y_CD - 15;
  // Charted-depth reading point — deep water, vessel sits above it.
  const DEPTH_X = 110;
  const DEPTH_SEABED_Y = Y_CD + 47;
  // Vessel position
  const VESSEL_X = 110;
  const VESSEL_KEEL_Y = Y_WATER + 28;
  // Bridge piers
  const BRIDGE_LEFT_X = 220;
  const BRIDGE_RIGHT_X = 540;

  return (
    <svg
      viewBox="-80 0 760 480"
      width="100%"
      style={{ display: "block", maxWidth: size, height: "auto" }}
      role="img"
      aria-label="Tidal heights cross-section showing chart datum, the eight tidal levels, and the standard measurements (charted depth, drying height, height of tide, sounding, vertical clearance)."
    >
      {/* Sky */}
      <rect x="-80" y="0" width="760" height={Y_WATER} fill="#dbeafe" />
      {/* Sea (water above seabed) */}
      <rect
        x="-80"
        y={Y_WATER}
        width="760"
        height={480 - Y_WATER}
        fill="#9cc3e0"
        opacity="0.6"
      />
      {/* Seabed */}
      <path d={seabedPath} fill="#caa86b" stroke="#7d6a44" strokeWidth="1" />

      {/* Bridge — flat deck spanning the channel, piers descend
          from the underside of the deck DOWN to where they meet
          the seabed / coastline (left pier in deep water; right
          pier on the rising shore). */}
      <rect
        x={BRIDGE_LEFT_X - 4}
        y={Y_BRIDGE_BOTTOM - 6}
        width={BRIDGE_RIGHT_X - BRIDGE_LEFT_X + 8}
        height="6"
        fill="#1c2530"
      />
      {/* Left pier — into deep water, lands on the seabed ~y=290. */}
      <rect
        x={BRIDGE_LEFT_X}
        y={Y_BRIDGE_BOTTOM}
        width="10"
        height={290 - Y_BRIDGE_BOTTOM}
        fill="#1c2530"
      />
      {/* Right pier — lands on rising shoreline ~y=230. */}
      <rect
        x={BRIDGE_RIGHT_X - 10}
        y={Y_BRIDGE_BOTTOM}
        width="10"
        height={230 - Y_BRIDGE_BOTTOM}
        fill="#1c2530"
      />

      {/* Tidal-level horizontal lines. HAT + LAT are dashed (extremes);
          the four mean tides + ML are solid; CD is double-line. */}
      <g
        stroke="#0f172a"
        strokeWidth="0.6"
        fontSize="10"
        fontFamily="ui-sans-serif, system-ui"
        fill="#0f172a"
      >
        <line x1="-80" y1={Y_HAT} x2="680" y2={Y_HAT} strokeDasharray="4 4" />
        <line x1="-80" y1={Y_MHWS} x2="680" y2={Y_MHWS} />
        <line x1="-80" y1={Y_MHWN} x2="680" y2={Y_MHWN} />
        <line
          x1="-80"
          y1={Y_ML}
          x2="680"
          y2={Y_ML}
          stroke="#475569"
          strokeDasharray="2 4"
        />
        <line x1="-80" y1={Y_MLWN} x2="680" y2={Y_MLWN} />
        <line x1="-80" y1={Y_MLWS} x2="680" y2={Y_MLWS} />
        <line x1="-80" y1={Y_LAT} x2="680" y2={Y_LAT} strokeDasharray="4 4" />
        {/* Chart Datum — drawn as a heavier double line just below LAT */}
        <line x1="-80" y1={Y_CD} x2="680" y2={Y_CD} strokeWidth="1.2" />
        <line x1="-80" y1={Y_CD + 2} x2="680" y2={Y_CD + 2} strokeWidth="1.2" />
      </g>

      {/* Right-side level labels */}
      <g
        fontSize="11"
        fontFamily="ui-sans-serif, system-ui"
        fontWeight="600"
        fill="#0f172a"
      >
        <text x="608" y={Y_HAT + 3}>
          HAT
        </text>
        <text x="608" y={Y_MHWS + 3}>
          MHWS
        </text>
        <text x="608" y={Y_MHWN + 3}>
          MHWN
        </text>
        <text x="608" y={Y_ML + 3} fill="#475569">
          ML
        </text>
        <text x="608" y={Y_MLWN + 3}>
          MLWN
        </text>
        <text x="608" y={Y_MLWS + 3}>
          MLWS
        </text>
        <text x="608" y={Y_LAT + 3}>
          LAT
        </text>
        <text x="608" y={Y_CD + 12} fontWeight="700">
          CD
        </text>
      </g>

      {/* Current water-surface line (over MHWN, illustrative) */}
      <line
        x1="0"
        y1={Y_WATER}
        x2="600"
        y2={Y_WATER}
        stroke="#1e6091"
        strokeWidth="1.5"
      />
      <text
        x="6"
        y={Y_WATER - 4}
        fontSize="10"
        fill="#1e6091"
        fontFamily="ui-sans-serif, system-ui"
        fontWeight="600"
      >
        Water surface NOW
      </text>

      {/* Vessel — small yacht profile */}
      <g transform={`translate(${VESSEL_X} ${Y_WATER})`}>
        {/* Hull */}
        <path
          d="M -22 0 L 22 0 L 18 12 Q 0 22 -18 12 Z"
          fill="#1c2530"
          stroke="#0f172a"
          strokeWidth="0.6"
        />
        {/* Mast */}
        <line x1="0" y1="0" x2="0" y2="-35" stroke="#0f172a" strokeWidth="1.5" />
        {/* Keel */}
        <path
          d="M -3 12 L 3 12 L 5 28 L -5 28 Z"
          fill="#1c2530"
        />
      </g>

      {/* ── Annotation arrows on the LEFT side ─────────────────── */}

      {/* Charted depth: from CD down to seabed under the deep-water
          column on the left (x = DEPTH_X) */}
      <Arrow
        x1={DEPTH_X - 38}
        y1={Y_CD}
        x2={DEPTH_X - 38}
        y2={DEPTH_SEABED_Y}
        label="Charted depth"
        labelX={DEPTH_X - 95}
        labelY={(Y_CD + DEPTH_SEABED_Y) / 2 - 2}
        sublabel="(below CD)"
        anchor="end"
      />

      {/* Height of tide: from CD up to current water surface */}
      <Arrow
        x1={DEPTH_X + 50}
        y1={Y_WATER}
        x2={DEPTH_X + 50}
        y2={Y_CD}
        label="Height of tide"
        labelX={DEPTH_X + 88}
        labelY={(Y_WATER + Y_CD) / 2 - 2}
        sublabel="(above CD)"
        anchor="start"
      />

      {/* Sounding (actual depth): from water surface to seabed */}
      <Arrow
        x1={DEPTH_X}
        y1={Y_WATER + 30}
        x2={DEPTH_X}
        y2={DEPTH_SEABED_Y}
        stroke="#3aa055"
        label="Sounding"
        labelX={DEPTH_X + 8}
        labelY={(Y_WATER + DEPTH_SEABED_Y) / 2 + 18}
        sublabel="(= charted depth + HoT)"
        anchor="start"
      />

      {/* Drying height: from CD up to top of drying rock */}
      <Arrow
        x1={DRYING_X + 24}
        y1={Y_CD}
        x2={DRYING_X + 24}
        y2={DRYING_TOP_Y}
        stroke="#a16207"
        label="Drying height"
        labelX={DRYING_X + 32}
        labelY={(Y_CD + DRYING_TOP_Y) / 2 + 2}
        sublabel="(above CD, underlined on chart)"
        anchor="start"
      />

      {/* Vertical clearance: from water surface to bridge underside */}
      <Arrow
        x1={420}
        y1={Y_WATER}
        x2={420}
        y2={Y_BRIDGE_BOTTOM}
        stroke="#1e6091"
        label="Vertical clearance"
        labelX={432}
        labelY={(Y_WATER + Y_BRIDGE_BOTTOM) / 2 + 2}
        sublabel="(now — chart shows MHWS clearance)"
        anchor="start"
      />

      {/* Spring range: tied between MHWS and MLWS far left */}
      <Arrow
        x1={42}
        y1={Y_MHWS}
        x2={42}
        y2={Y_MLWS}
        stroke="#7c3aed"
        label="Springs range"
        labelX={50}
        labelY={(Y_MHWS + Y_MLWS) / 2 + 2}
        sublabel="(MHWS − MLWS)"
        anchor="start"
      />

      {/* Sample values on the deep-water column */}
      <text
        x={DEPTH_X - 100}
        y={DEPTH_SEABED_Y - 4}
        fontSize="10"
        fill="#5d6878"
        textAnchor="end"
        fontFamily="ui-sans-serif, system-ui"
      >
        e.g. 7.4 m
      </text>
      <text
        x={DEPTH_X + 93}
        y={Y_WATER - 4}
        fontSize="10"
        fill="#5d6878"
        textAnchor="start"
        fontFamily="ui-sans-serif, system-ui"
      >
        e.g. 4.5 m
      </text>
      <text
        x={DEPTH_X + 13}
        y={Y_WATER + 50}
        fontSize="10"
        fill="#2c7a3b"
        textAnchor="start"
        fontFamily="ui-sans-serif, system-ui"
      >
        = 11.9 m under the keel
      </text>

      {/* Drying-feature label */}
      <text
        x={DRYING_X}
        y={DRYING_TOP_Y - 6}
        fontSize="10"
        fill="#7d6a44"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui"
        fontStyle="italic"
      >
        drying rock
      </text>

      {/* Bridge label */}
      <text
        x={(BRIDGE_LEFT_X + BRIDGE_RIGHT_X) / 2}
        y={Y_BRIDGE_BOTTOM - 12}
        fontSize="11"
        fill="#0f172a"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui"
        fontWeight="600"
      >
        Bridge (clearance shown above MHWS)
      </text>
    </svg>
  );
}

function Arrow({
  x1,
  y1,
  x2,
  y2,
  stroke = "#0f172a",
  label,
  labelX,
  labelY,
  sublabel,
  anchor,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  stroke?: string;
  label: string;
  labelX: number;
  labelY: number;
  sublabel?: string;
  anchor: "start" | "end" | "middle";
}) {
  // Double-headed arrow drawn as a line with two arrowhead markers
  // synthesised inline — easier than wiring up <defs><marker>.
  const head = 4;
  const dy = y2 > y1 ? 1 : -1;
  return (
    <g
      stroke={stroke}
      fill={stroke}
      strokeWidth="1"
      fontFamily="ui-sans-serif, system-ui"
    >
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      {/* Top head */}
      <polygon
        points={`${x1},${y1} ${x1 - head},${y1 + head * dy} ${x1 + head},${y1 + head * dy}`}
      />
      {/* Bottom head */}
      <polygon
        points={`${x2},${y2} ${x2 - head},${y2 - head * dy} ${x2 + head},${y2 - head * dy}`}
      />
      <text
        x={labelX}
        y={labelY}
        fontSize="11"
        textAnchor={anchor}
        stroke="none"
        fontWeight="600"
      >
        {label}
      </text>
      {sublabel && (
        <text
          x={labelX}
          y={labelY + 12}
          fontSize="9"
          textAnchor={anchor}
          stroke="none"
          fill="#5d6878"
          fontWeight="400"
        >
          {sublabel}
        </text>
      )}
    </g>
  );
}
